import {
  Box,
  Circle,
  Flex,
  Grid,
  HStack,
  Skeleton,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import React, { useCallback, useRef, useState } from "react";

import { useAiInterviewerMetricsQuery } from "../../graphql";

export type MetricCardProps = {
  label: string;
  children: React.ReactNode;
  loading?: boolean;
};

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  children,
  loading,
}) => (
  <Flex direction="column" gap="2" pt="5" pb="4" px="6" flex="1">
    <Text
      fontSize="11.67px"
      fontWeight="600"
      color="#808B97"
      textTransform="uppercase"
      letterSpacing="0"
      lineHeight="16px"
      minH="32px"
    >
      {label}
    </Text>
    {loading ? (
      <Skeleton height="60px" width="80px" />
    ) : (
      <Flex alignItems="baseline" gap="0.5">
        {children}
      </Flex>
    )}
  </Flex>
);

export const MetricValue: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Text
    fontSize="40px"
    fontWeight="400"
    color="#393F47"
    lineHeight="60px"
    letterSpacing="0"
  >
    {children}
  </Text>
);

export const MetricSuffix: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Text fontSize="14px" fontWeight="500" color="#060E14" lineHeight="20px">
    {children}
  </Text>
);

const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

const formatTimeSaved = (
  totalSeconds: number
): { days: number; hours: number } => {
  const totalHours = Math.floor(totalSeconds / 3600);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return { days, hours };
};

export type PaginationDotsProps = {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
};

export const PaginationDots: React.FC<PaginationDotsProps> = ({
  totalPages,
  currentPage,
  onPageChange,
}) => (
  <HStack spacing="2" justify="center" pt="2">
    {Array.from({ length: totalPages }).map((_, index) => (
      <Circle
        // eslint-disable-next-line react/no-array-index-key
        key={`page-${index}`}
        size="8px"
        bg={index === currentPage ? "blue.500" : "#D3D8DE"}
        cursor="pointer"
        onClick={() => onPageChange(index)}
        transition="background 0.2s"
      />
    ))}
  </HStack>
);

export const AiInterviewerMetricsCards: React.FC = () => {
  const { data, loading } = useAiInterviewerMetricsQuery({
    fetchPolicy: "cache-and-network",
  });
  const [currentPage, setCurrentPage] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const metrics = data?.aiInterviewerMetrics;
  const timeSaved = formatTimeSaved(metrics?.recruiterTimeSavedSeconds ?? 0);

  const totalPages = 2; // 4 metrics, 2 per page

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, clientWidth } = scrollContainerRef.current;
    const newPage = Math.round(scrollLeft / clientWidth);
    setCurrentPage(newPage);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    if (!scrollContainerRef.current) return;
    const { clientWidth } = scrollContainerRef.current;
    scrollContainerRef.current.scrollTo({
      left: page * clientWidth,
      behavior: "smooth",
    });
  }, []);

  // Desktop: 4-column grid
  if (!isMobile) {
    return (
      <Box
        bg="white"
        border="1px solid"
        borderColor="#D3D8DE"
        borderRadius="12px"
        mb="6"
      >
        <Grid templateColumns="repeat(4, 1fr)">
          <MetricCard label="Active" loading={loading}>
            <MetricValue>
              {formatNumber(metrics?.activeInterviewersCount ?? 0)}
            </MetricValue>
          </MetricCard>

          <MetricCard label="Draft" loading={loading}>
            <MetricValue>
              {formatNumber(metrics?.draftInterviewersCount ?? 0)}
            </MetricValue>
          </MetricCard>

          <MetricCard label="Interviews completed" loading={loading}>
            <MetricValue>
              {formatNumber(metrics?.completedInterviewsCount ?? 0)}
            </MetricValue>
          </MetricCard>

          <MetricCard label="Time saved" loading={loading}>
            <Flex alignItems="baseline" gap="0.5">
              <MetricValue>{timeSaved.days}</MetricValue>
              <MetricSuffix>days</MetricSuffix>
            </Flex>
            <Flex alignItems="baseline" gap="0.5" ml="2">
              <MetricValue>{timeSaved.hours}</MetricValue>
              <MetricSuffix>hrs</MetricSuffix>
            </Flex>
          </MetricCard>
        </Grid>
      </Box>
    );
  }

  // Mobile: Swipeable 2-per-page with pagination dots
  return (
    <Box mb="6">
      <Box
        bg="white"
        border="1px solid"
        borderColor="#D3D8DE"
        borderRadius="12px"
        overflow="hidden"
      >
        <Box
          ref={scrollContainerRef}
          overflowX="auto"
          onScroll={handleScroll}
          css={{
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Flex width={`${totalPages * 100}%`}>
            {/* Page 1: Active & Draft */}
            <Grid
              templateColumns="repeat(2, 1fr)"
              flex="0 0 50%"
              css={{ scrollSnapAlign: "start" }}
            >
              <MetricCard label="Active" loading={loading}>
                <MetricValue>
                  {formatNumber(metrics?.activeInterviewersCount ?? 0)}
                </MetricValue>
              </MetricCard>
              <MetricCard label="Draft" loading={loading}>
                <MetricValue>
                  {formatNumber(metrics?.draftInterviewersCount ?? 0)}
                </MetricValue>
              </MetricCard>
            </Grid>

            {/* Page 2: Completed & Time Saved */}
            <Grid
              templateColumns="repeat(2, 1fr)"
              flex="0 0 50%"
              css={{ scrollSnapAlign: "start" }}
            >
              <MetricCard label="Completed" loading={loading}>
                <MetricValue>
                  {formatNumber(metrics?.completedInterviewsCount ?? 0)}
                </MetricValue>
              </MetricCard>
              <MetricCard label="Time saved" loading={loading}>
                <Flex alignItems="baseline" gap="0.5">
                  <MetricValue>{timeSaved.days}</MetricValue>
                  <MetricSuffix>days</MetricSuffix>
                </Flex>
                <Flex alignItems="baseline" gap="0.5" ml="2">
                  <MetricValue>{timeSaved.hours}</MetricValue>
                  <MetricSuffix>hrs</MetricSuffix>
                </Flex>
              </MetricCard>
            </Grid>
          </Flex>
        </Box>
      </Box>
      <PaginationDots
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};
